"use client";

import React, { useState, useRef, useEffect } from "react";
import Image from "next/image";
import SmoothImage from "@/components/SmoothImage";
import { renderTextWithBreaks } from "@/utils/stripHTML";
import { motion } from "framer-motion";
import { useFullPageContext } from "@/context/FullPageContext";

export interface StorySplitItem {
  id: number;
  tag: string;
  highlightText?: string;
  videourl?: string;
  title: string;
  subtitle?: string;
  description: string;
  image: string;
}

interface StorySplitSectionProps {
  data: StorySplitItem;
  screenIndex: number;
}

function formatVideoUrl(url: string = ""): {
  formattedUrl: string;
  isDirectVideo: boolean;
} {
  if (!url) return { formattedUrl: "", isDirectVideo: false };
  let cleanUrl = url.trim();

  const isDirect =
    cleanUrl.endsWith(".mp4") ||
    cleanUrl.endsWith(".webm") ||
    cleanUrl.endsWith(".ogg");

  if (isDirect) {
    return { formattedUrl: cleanUrl, isDirectVideo: true };
  }

  let videoId = "";
  if (cleanUrl.includes("youtube.com/watch?v=")) {
    videoId = cleanUrl.split("v=")[1]?.split("&")[0] || "";
  } else if (cleanUrl.includes("youtu.be/")) {
    videoId = cleanUrl.split("youtu.be/")[1]?.split("?")[0] || "";
  } else if (cleanUrl.includes("youtube.com/embed/")) {
    videoId = cleanUrl.split("youtube.com/embed/")[1]?.split("?")[0] || "";
  }

  if (videoId) {
    return {
      formattedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&iv_load_policy=3&enablejsapi=1&playsinline=1`,
      isDirectVideo: false,
    };
  }

  return { formattedUrl: cleanUrl, isDirectVideo: false };
}

export default function StorySplitSection({
  data,
  screenIndex,
}: StorySplitSectionProps) {
  const { activeIndex, isFullPage } = useFullPageContext();

  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const videoRef = useRef<HTMLVideoElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const userPausedRef = useRef(false);
  const userMutedRef = useRef(false);
  const hasAutoUnmutedRef = useRef(false);

  if (!data) return null;

  // Check if activeIndex is within Story Split Screens range (Index 1, 2, 3)
  const isStoryRange = activeIndex >= 1 && activeIndex <= 3;

  // Counter-translation offset for desktop
  const counterOffsetY =
    isStoryRange && isFullPage ? (activeIndex - screenIndex) * 100 : 0;

  // Text active state for smooth fade in / fade out
  const isTextActive = activeIndex === screenIndex;

  const { formattedUrl: formattedVideoUrl, isDirectVideo } = formatVideoUrl(
    data.videourl || "",
  );

  const sendYouTubeCommand = (func: string, args: any[] = []) => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        JSON.stringify({
          event: "command",
          func: func,
          args: args,
        }),
        "*",
      );
    }
  };

  const pauseVideo = () => {
    setIsPlaying(false);
    if (isDirectVideo) {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    } else {
      sendYouTubeCommand("pauseVideo", []);
    }
  };

  const playVideo = () => {
    setIsPlaying(true);
    if (isDirectVideo) {
      if (videoRef.current) {
        videoRef.current.play().catch(() => {});
      }
    } else {
      sendYouTubeCommand("playVideo", []);
    }
  };

  const togglePlay = () => {
    if (isPlaying) {
      userPausedRef.current = true;
      pauseVideo();
    } else {
      userPausedRef.current = false;
      playVideo();
    }
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
    const shouldMute = newVolume === 0;
    setIsMuted(shouldMute);
    userMutedRef.current = shouldMute;

    if (isDirectVideo) {
      if (videoRef.current) {
        videoRef.current.volume = newVolume;
        videoRef.current.muted = shouldMute;
      }
    } else {
      sendYouTubeCommand("setVolume", [Math.round(newVolume * 100)]);
      if (shouldMute) {
        sendYouTubeCommand("mute", []);
      } else {
        sendYouTubeCommand("unMute", []);
      }
    }
  };

  const toggleMute = () => {
    const nextMutedState = !isMuted;
    setIsMuted(nextMutedState);
    userMutedRef.current = nextMutedState;

    if (nextMutedState) {
      if (isDirectVideo) {
        if (videoRef.current) videoRef.current.muted = true;
      } else {
        sendYouTubeCommand("mute", []);
      }
    } else {
      const activeVolume = volume === 0 ? 0.5 : volume;
      if (volume === 0) setVolume(0.5);

      if (isDirectVideo) {
        if (videoRef.current) {
          videoRef.current.muted = false;
          videoRef.current.volume = activeVolume;
        }
      } else {
        sendYouTubeCommand("unMute", []);
        sendYouTubeCommand("setVolume", [Math.round(activeVolume * 100)]);
      }
    }
  };

  // Pause video when section scrolls out of view, auto unmute at 50% volume on first scroll into view (unless user muted)
  useEffect(() => {
    if (!data.videourl || !formattedVideoUrl) return;

    const applyAutoUnmute = () => {
      if (userMutedRef.current) return;

      const targetVol = volume > 0 ? volume : 0.5;
      setVolume(targetVol);
      setIsMuted(false);

      if (isDirectVideo) {
        if (videoRef.current) {
          videoRef.current.volume = targetVol;
          videoRef.current.muted = false;
        }
      } else {
        sendYouTubeCommand("unMute", []);
        sendYouTubeCommand("setVolume", [Math.round(targetVol * 100)]);
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          pauseVideo();
        } else {
          if (!userPausedRef.current) {
            playVideo();
          }
          if (!hasAutoUnmutedRef.current) {
            hasAutoUnmutedRef.current = true;
            applyAutoUnmute();
          } else if (!userMutedRef.current) {
            applyAutoUnmute();
          }
        }
      },
      { threshold: 0.15 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    const handleFirstGesture = () => {
      if (!userMutedRef.current) {
        applyAutoUnmute();
      }
    };

    window.addEventListener("pointerdown", handleFirstGesture, { once: true });
    window.addEventListener("keydown", handleFirstGesture, { once: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("pointerdown", handleFirstGesture);
      window.removeEventListener("keydown", handleFirstGesture);
    };
  }, [data.videourl, isDirectVideo, formattedVideoUrl, volume]);

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-h-[auto] lg:h-screen flex flex-col lg:flex-row overflow-hidden bg-secondary-light lg:bg-primaryDark select-none"
    >
      {/* IMAGE / VIDEO CONTAINER (LEFT ON DESKTOP, SECOND ON MOBILE/TABLET) */}
      <div className="w-full lg:w-[65%] order-2 lg:order-1 aspect-video sm:aspect-[16/9.5] lg:aspect-auto lg:h-full relative overflow-hidden flex-shrink-0 flex items-center justify-center bg-black/40">
        <motion.div
          initial={{ scale: 1.05 }}
          animate={{ scale: isFullPage ? (isTextActive ? 1 : 1.08) : 1 }}
          transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
          className="relative w-full h-full flex items-center justify-center"
        >
          {data.videourl && formattedVideoUrl ? (
            isDirectVideo ? (
              <video
                ref={videoRef}
                src={formattedVideoUrl}
                poster={data.image}
                autoPlay
                loop
                muted={isMuted}
                playsInline
                className="w-full h-full object-cover object-center"
              />
            ) : (
              <iframe
                ref={iframeRef}
                src={formattedVideoUrl}
                title={data.title || "Story Background Video"}
                allow="autoplay; encrypted-media"
                className="w-full h-full border-none pointer-events-none scale-105 sm:scale-105 lg:scale-[1.7] object-cover origin-center"
              />
            )
          ) : (
            <SmoothImage
              src={data.image}
              alt={data.title}
              fill
              sizes="(max-width: 1024px) 100vw, 65vw"
              priority
              className="object-cover object-center"
            />
          )}
        </motion.div>

        {/* VIDEO CONTROLS OVERLAY */}
        {data.videourl && formattedVideoUrl && (
          <div className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 z-30 flex items-center gap-3 pointer-events-auto">
            {/* PLAY / PAUSE BUTTON */}
            <button
              type="button"
              onClick={togglePlay}
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#053328]/80 hover:bg-[#053328] border border-white/20 hover:border-[#E5CFB0] text-white flex items-center justify-center backdrop-blur-md shadow-lg transition-all transform hover:scale-105 active:scale-95 cursor-pointer group"
              aria-label={isPlaying ? "Dừng video" : "Phát video"}
              title={isPlaying ? "Dừng video" : "Phát video"}
            >
              {isPlaying ? (
                <svg
                  className="w-5 h-5 fill-current text-white group-hover:text-[#E5CFB0] transition-colors"
                  viewBox="0 0 24 24"
                >
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5 fill-current text-white group-hover:text-[#E5CFB0] transition-colors ml-0.5"
                  viewBox="0 0 24 24"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>

            {/* VOLUME CONTROL BAR (ALWAYS VISIBLE SLIDER) */}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#053328]/80 hover:bg-[#053328] border border-white/20 hover:border-[#E5CFB0] backdrop-blur-md shadow-lg transition-all">
              <button
                type="button"
                onClick={toggleMute}
                className="w-7 h-7 text-white flex items-center justify-center transition-all transform hover:scale-110 active:scale-95 cursor-pointer group"
                aria-label={isMuted || volume === 0 ? "Bật tiếng" : "Tắt tiếng"}
                title={isMuted || volume === 0 ? "Bật tiếng" : "Tắt tiếng"}
              >
                {isMuted || volume === 0 ? (
                  <svg
                    className="w-5 h-5 fill-current text-white group-hover:text-[#E5CFB0] transition-colors"
                    viewBox="0 0 24 24"
                  >
                    <path d="M4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73 4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                  </svg>
                ) : volume < 0.5 ? (
                  <svg
                    className="w-5 h-5 fill-current text-white group-hover:text-[#E5CFB0] transition-colors"
                    viewBox="0 0 24 24"
                  >
                    <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z" />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5 fill-current text-white group-hover:text-[#E5CFB0] transition-colors"
                    viewBox="0 0 24 24"
                  >
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                  </svg>
                )}
              </button>

              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={isMuted ? 0 : volume}
                onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                className="w-20 sm:w-24 h-1.5 bg-white/30 rounded-lg appearance-none cursor-pointer accent-[#E5CFB0] hover:bg-white/40 transition-colors"
                aria-label="Thanh điều chỉnh âm lượng"
              />
            </div>
          </div>
        )}
      </div>

      {/* PAPER TEXTURE BEIGE PANEL (RIGHT ON DESKTOP, FIRST ON MOBILE/TABLET) */}
      <div
        className="w-full lg:w-[35%] order-1 lg:order-2 min-h-[auto] lg:h-full bg-secondary-light text-primaryDark flex flex-col justify-center px-6 sm:px-8 md:px-10 lg:px-10 xl:px-12 py-10 sm:py-12 lg:py-10 relative overflow-hidden transition-transform duration-700 ease-in-out"
        style={{
          transform: isFullPage ? `translateY(${counterOffsetY}vh)` : undefined,
        }}
      >
        {/* PAPER TEXTURE BACKGROUND IMAGE */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <SmoothImage
            src="/images/background_2.png"
            alt="Paper Texture Background"
            fill
            sizes="(max-width: 1024px) 100vw, 35vw"
            priority
            className="object-cover object-center"
          />
        </div>

        {/* TEXT CONTENT WITH SMOOTH FADE-IN / FADE-OUT */}
        <motion.div
          initial={{ opacity: isFullPage ? 0 : 1 }}
          animate={{ opacity: isFullPage ? (isTextActive ? 1 : 0) : 1 }}
          transition={{ duration: 0.45, ease: "easeInOut" }}
          className="max-w-xl space-y-3.5 sm:space-y-4 md:space-y-5 relative z-10"
        >
          {/* 1. TAG / CATEGORY */}
          <div className="flex items-center gap-3">
            <span className="w-6 h-[1px] bg-primaryDark/40" />
            <span className="text-[11px] md:text-xs font-semibold uppercase tracking-[0.22em] text-primaryDark/70 font-sans">
              {data.tag}
            </span>
          </div>

          {/* 2. HIGHLIGHT TEXT */}
          {data.highlightText && (
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-lora font-normal text-primaryDark leading-tight tracking-tight pt-1">
              {renderTextWithBreaks(data.highlightText)}
            </h1>
          )}

          {/* 3. MAIN TITLE */}
          <h2
            className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-lora font-normal text-primaryDark tracking-tight title-leading"
            style={{ lineHeight: 1.15 }}
          >
            {renderTextWithBreaks(data.title)}
          </h2>

          {/* 4. DESCRIPTION */}
          <p className="text-xs sm:text-sm md:text-base font-light text-primaryDark/80 leading-relaxed font-sans max-w-lg pt-1">
            {renderTextWithBreaks(data.description)}
          </p>

          {/* 5. LINEAR OPACITY DIVIDER LINE FROM LEFT TO RIGHT */}
          <div className="w-full h-[1px] bg-gradient-to-r from-primaryDark/30 via-primaryDark/15 to-transparent " />

          {/* 6. ORANGE/TAN ITALIC TEXT AT BOTTOM (SUBTITLE) */}
          {data.subtitle && (
            <p className="text-xs sm:text-sm font-lora italic text-third leading-relaxed pt-1">
              {renderTextWithBreaks(data.subtitle)}
            </p>
          )}
        </motion.div>
      </div>
    </section>
  );
}
